/**
 * Sri Sai Pooja Samagri (SSPS) - Content Pages & Policies Helper
 *
 * Lightweight, zero-dependency enhancements for:
 * 1. Policy navigation and table of contents
 * 2. Accessible table wrapping for rich text (preventing horizontal scroll)
 * 3. Client-side FAQ search filtering across existing questions
 * 4. Safe WebView link navigation
 */
(function () {
  'use strict';

  // 1. Wrap all tables in .rte and policy bodies with responsive scroll containers (ignoring cart/checkout/header)
  function setupResponsiveTables() {
    const tables = document.querySelectorAll(
      '.shopify-policy__body table, .article-template__content table, .main-page-content table, .ssps-content-body table, .rte table'
    );
    tables.forEach((table) => {
      // Guard against cart, mini-cart drawer, customer tables, or already wrapped tables
      if (
        table.closest('.cart') ||
        table.closest('.cart-drawer') ||
        table.closest('.mini-cart') ||
        table.closest('.header') ||
        table.closest('header') ||
        table.closest('form') ||
        (table.parentElement && table.parentElement.classList.contains('ssps-table-scroll-wrapper'))
      ) {
        return;
      }
      const wrapper = document.createElement('div');
      wrapper.className = 'ssps-table-scroll-wrapper';
      wrapper.style.cssText = 'width: 100%; max-width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; margin: 20px 0; border: 1px solid #E5E7EB; border-radius: 10px; background: #fff;';
      table.parentNode.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    });
  }

  // 2. Client-side FAQ Live Search (Instant filter across existing FAQ questions & answers)
  function setupFaqSearch() {
    const faqSearchInput = document.getElementById('ssps-faq-search-input');
    const faqClearBtn = document.getElementById('ssps-faq-search-clear');
    if (!faqSearchInput) return;

    const faqItems = document.querySelectorAll('.faq__list--item');
    const faqSections = document.querySelectorAll('.faq__wrapper');

    function filterFaqs() {
      const query = faqSearchInput.value.trim().toLowerCase();
      if (faqClearBtn) {
        faqClearBtn.style.display = query.length > 0 ? 'inline-flex' : 'none';
      }

      let totalMatches = 0;

      faqItems.forEach((item) => {
        const heading = (item.querySelector('.faq__list--item--heading')?.textContent || '').toLowerCase();
        const content = (item.querySelector('.faq__list--item--content')?.textContent || '').toLowerCase();
        const matches = query === '' || heading.includes(query) || content.includes(query);

        if (matches) {
          item.style.display = '';
          totalMatches++;
          if (query.length > 1) {
            item.classList.add('active');
            const body = item.querySelector('.faq__body');
            if (body) body.style.display = 'block';
          }
        } else {
          item.style.display = 'none';
        }
      });

      // Show/hide empty state message
      let noResultMsg = document.getElementById('ssps-faq-no-results');
      if (!noResultMsg) {
        noResultMsg = document.createElement('div');
        noResultMsg.id = 'ssps-faq-no-results';
        noResultMsg.className = 'text-center';
        noResultMsg.style.cssText = 'padding: 40px 20px; font-size: 15px; color: #6B7280; display: none;';
        noResultMsg.innerHTML = '<p style="margin-bottom: 8px; font-weight: 700; color: #1F2937; font-size: 17px;">No matching questions found</p><p>Try searching for a different keyword or contact our support team on WhatsApp.</p>';
        const container = document.querySelector('.faq__wrapper .container, .faq__wrapper .container-fluid');
        if (container) container.appendChild(noResultMsg);
      }

      if (noResultMsg) {
        noResultMsg.style.display = (query.length > 0 && totalMatches === 0) ? 'block' : 'none';
      }
    }

    faqSearchInput.addEventListener('input', filterFaqs);
    if (faqClearBtn) {
      faqClearBtn.addEventListener('click', function () {
        faqSearchInput.value = '';
        filterFaqs();
        faqSearchInput.focus();
      });
    }
  }

  // 3. Accessibility enhancements for FAQ accordion buttons
  function setupFaqAccessibility() {
    const faqButtons = document.querySelectorAll('.faq__list--item__button');
    faqButtons.forEach((btn, index) => {
      btn.setAttribute('type', 'button');
      const item = btn.closest('.faq__list--item');
      const body = item?.querySelector('.faq__body');
      if (body) {
        const bodyId = body.id || 'faq-body-' + index;
        body.id = bodyId;
        body.setAttribute('role', 'region');
        btn.setAttribute('aria-controls', bodyId);
        btn.setAttribute('aria-expanded', item.classList.contains('active') ? 'true' : 'false');
      }

      btn.addEventListener('click', function () {
        setTimeout(() => {
          const isExpanded = item.classList.contains('active');
          btn.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        }, 50);
      });
    });
  }

  // 4. Policy page "On this page" TOC generator (only if policy has >= 3 genuine headings)
  function setupPolicyTableOfContents() {
    const policyBody = document.querySelector('.shopify-policy__body .rte');
    if (!policyBody) return;

    const headings = policyBody.querySelectorAll('h2, h3');
    if (headings.length < 3) return;

    if (document.getElementById('ssps-policy-toc')) return;

    const tocCard = document.createElement('nav');
    tocCard.id = 'ssps-policy-toc';
    tocCard.className = 'ssps-policy-toc-card';
    tocCard.setAttribute('aria-label', 'Table of Contents');
    tocCard.style.cssText = 'background: #FDFBF7; border: 1px solid #F3EDE2; border-radius: 12px; padding: 18px 22px; margin-bottom: 30px;';

    let tocHtml = '<div style="font-weight: 700; font-size: 13.5px; text-transform: uppercase; letter-spacing: 0.05em; color: #6B0F1A; margin-bottom: 12px; display: flex; align-items: center; gap: 8px;"><span>📋</span><span>On this page</span></div><ul style="list-style: none; padding-left: 0; margin: 0; display: flex; flex-direction: column; gap: 8px;">';

    headings.forEach((h, i) => {
      const id = h.id || 'policy-section-' + (i + 1);
      h.id = id;
      const text = h.textContent.trim();
      const isSub = h.tagName.toLowerCase() === 'h3';
      tocHtml += `<li style="margin: 0; padding-left: ${isSub ? '16px' : '0'};">
        <a href="#${id}" style="color: #4B5563; font-size: 14px; font-weight: ${isSub ? '500' : '600'}; text-decoration: none; transition: color 0.15s ease;">${text}</a>
      </li>`;
    });

    tocHtml += '</ul>';
    tocCard.innerHTML = tocHtml;

    policyBody.insertBefore(tocCard, policyBody.firstChild);
  }

  // 5. Article "On this page" collapsible Table of Contents
  function setupArticleTableOfContents() {
    const articleBody = document.querySelector('.article-template__content.rte');
    if (!articleBody) return;

    const headings = articleBody.querySelectorAll('h2, h3');
    if (headings.length < 3) return;

    if (document.getElementById('ssps-article-toc')) return;

    const isDesktop = window.innerWidth >= 768;
    const tocDetails = document.createElement('details');
    tocDetails.id = 'ssps-article-toc';
    tocDetails.className = 'ssps-article-toc';
    if (isDesktop) tocDetails.setAttribute('open', '');
    tocDetails.style.cssText = 'background: #FFFDF9; border: 1.5px solid #F3EDE2; border-radius: 12px; padding: 14px 18px; margin: 20px 0 32px;';

    let summaryHtml = '<summary style="font-weight: 700; font-size: 14px; color: #6B0F1A; cursor: pointer; display: flex; align-items: center; gap: 8px; user-select: none;"><span>📖</span><span>Table of Contents (On this page)</span></summary>';
    let tocListHtml = '<ul style="list-style: none; padding-left: 0; margin: 12px 0 0 0; display: flex; flex-direction: column; gap: 8px; border-top: 1px solid #F3EDE2; padding-top: 12px;">';

    headings.forEach((h, i) => {
      const id = h.id || 'article-section-' + (i + 1);
      h.id = id;
      const text = h.textContent.trim();
      const isSub = h.tagName.toLowerCase() === 'h3';
      tocListHtml += `<li style="margin: 0; padding-left: ${isSub ? '16px' : '0'};">
        <a href="#${id}" style="color: #4B5563; font-size: 14px; font-weight: ${isSub ? '500' : '600'}; text-decoration: none; transition: color 0.15s ease;">${text}</a>
      </li>`;
    });

    tocListHtml += '</ul>';
    tocDetails.innerHTML = summaryHtml + tocListHtml;

    articleBody.insertBefore(tocDetails, articleBody.firstChild);
  }

  // Initialize all enhancements on DOM ready
  function initContentPages() {
    setupResponsiveTables();
    setupFaqSearch();
    setupFaqAccessibility();
    setupPolicyTableOfContents();
    setupArticleTableOfContents();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initContentPages);
  } else {
    initContentPages();
  }
})();
